import { COOKIE_CONSENT_CONFIG } from '@/stores/entry_flow/config';
import { getCookie } from '@/utils/cookie';

import { CONSENT_REQUIRED_COOKIE, SESSION_STORAGE_KEY, SESSION_TTL_MS } from './config';
import type { EventIdentity } from './types';

/**
 * Tracker identity + consent self-gating (SB-6475).
 *
 * The tracker checks the `analytics` category of the consent cookie itself
 * rather than relying on GTM Consent Mode. This module is deliberately
 * Alpine-free: it runs from initTracker() before Alpine.start(), so user data
 * is read straight from window.siteData.global (never via the appStore, whose
 * import executes Alpine.store() and throws without siteData).
 *
 * Retention posture: the `sbTrackerSession` localStorage entry persists
 * indefinitely — SESSION_TTL_MS governs id *reuse*, not key removal, and
 * nothing clears it on consent revocation. Accepted: the value is a random
 * client UUID + timestamp (pseudonymous, no PII, never linked to trId at
 * rest), and an expired entry is inert — the next consented track()
 * overwrites it. Active removal on revocation would require a consent-change
 * hook, which the pre-plan rejected in favour of lazy re-checks.
 */

interface StoredSession {
    id: string;
    lastActivity: number;
}

/**
 * Consent gate for the tracker. Mirrors the platform's GTM gating pattern.
 */
export const hasAnalyticsConsent = (): boolean => {
    // Fail-open when `cookie_consent_required` is absent or not '1': this is
    // intentional and matches platform-wide GTM gating
    // (web/templates/partials/google_tag_manager.html:38) — the consent
    // machinery is not active in the jurisdiction. getCookie returns '' (not
    // null) for a missing cookie, so the !== '1' comparison covers it.
    if (getCookie(CONSENT_REQUIRED_COOKIE) !== '1') {
        return true;
    }

    // Fail closed: parseConsentCookie is a window global defined in
    // critical_js.html; its absence means the consent state is unknowable.
    // Throwing here would take down every track()/flush()/interval tick, so
    // log and drop instead.
    if (typeof window.parseConsentCookie !== 'function') {
        console.error('[tracker] parseConsentCookie missing — dropping events');
        return false;
    }

    // parseConsentCookie does atob()+JSON.parse() on the raw cookie and throws
    // on a malformed value (truncated, tampered, legacy format). Mirror the GTM
    // analytics path (google_tag_manager.html:32) and fail closed on a parse
    // error — an unguarded throw would escape track()/flush()/every interval
    // tick, exactly the outage the missing-function guard above avoids.
    try {
        return window.parseConsentCookie(getCookie(COOKIE_CONSENT_CONFIG.COOKIE_NAME))?.categories?.analytics || false;
    } catch (error) {
        console.error('[tracker] consent parse failed — dropping events', error);
        return false;
    }
};

/**
 * RFC-4122 v4 UUID. crypto.randomUUID needs a secure context and a modern
 * browser, hence the typeof guard (never a truthiness call) with a
 * getRandomValues fallback; no Math.random path.
 */
const generateUuid = (): string => {
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    // Set the v4 version and RFC-4122 variant bits.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Guards against foreign or corrupt writes to the session key: anything that
// is not { id: string, lastActivity: number } falls through to a fresh mint.
const isSessionShape = (value: unknown): value is StoredSession =>
    value !== null &&
    typeof value === 'object' &&
    typeof (value as StoredSession).id === 'string' &&
    typeof (value as StoredSession).lastActivity === 'number';

/**
 * Read the stored session safely, mirroring readJournal() (./queue.ts):
 * try/catch, JSON.parse, shape validation, safe default (null → mint).
 */
const readStoredSession = (): StoredSession | null => {
    try {
        const raw = localStorage.getItem(SESSION_STORAGE_KEY);
        if (!raw) return null;
        const data: unknown = JSON.parse(raw);
        return isSessionShape(data) ? data : null;
    } catch (error) {
        console.error('sbTracker: session read failed', error);
        return null;
    }
};

/**
 * Rolling 30-min cross-tab session id: reuse the stored id while within the
 * inactivity window (any track() counts as activity), else mint a new UUID.
 * Cross-tab sharing falls out of localStorage itself — read on every check,
 * no storage-event listener. Not rotated at login by construction: nothing
 * here reads or reacts to auth state.
 */
const getSessionId = (): string => {
    const now = Date.now();
    const stored = readStoredSession();
    const id = stored !== null && now - stored.lastActivity <= SESSION_TTL_MS ? stored.id : generateUuid();

    try {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id, lastActivity: now }));
    } catch (error) {
        // A failed write (quota, storage disabled) must never throw out of
        // track(): degrade to a per-page-load session, not a tracking outage.
        console.error('sbTracker: session write failed', error);
    }

    return id;
};

const getUserId = (): string | null => {
    const user = window.siteData?.global?.user;
    return user?.isLoggedIn ? (user.trId ?? null) : null;
};

export const getIdentity = (): EventIdentity => ({
    session_id: getSessionId(),
    user_id: getUserId(),
});
